import { EMode, TSeoKeywordData } from '@microfrontends/app-shell-v2'
import {
    ELanguage,
    Utils,
    Anchor,
    Span,
    colors,
    useIdentify,
    HogProvider,
    useHog
} from '@microfrontends/react-components'
import { useEffect, useRef, useState } from 'react'
import { IAuthUser } from '../../services/authService'
import { useStore } from '../../store'
import { ACTION_SET_LANGUAGE, ACTION_SET_USER } from '../../store/actions'
import AboutUs from '../AboutUs'
import Banner from '../Banner'
import { ContentWrapper } from '../Banner/Banner.css'
import { CompanyPageContainer, BreadcrumbStyle, ContentWithBordered } from './CompanyDetail.css'
import Benefit from '../Benefit'
import GotoTop from '../GotoTop'
import JobList from '../JobList'
import MasterLayout from '../MasterLayout'
import Video from '../Video'
import translations from './translation'
import { ICompanyInfo } from './type'
import { UPDATE_USER } from '../constants'
import { companyDetailService, TInfoCompanyOCI } from '../../services/companyDetailService'
import { TInfoPath, TMetaHead } from '../../helpers/urlHelper'
import NarrowArrow from '../Icons/NarrowArrow'

const msBaseUrl = process.env.NEXT_PUBLIC_MICROSERVICE_DOMAIN ?? ''
const EMPTY_BANNER_IMAGE = `${process.env.NEXT_PUBLIC_IMAGE_URL}/company-assets/images/banner-default-company.png`
const DELAY_HANDLER_TIME = 100
const isOptOutUserAgentFilter = process.env.NEXT_PUBLIC_POSTHOG_OPT_OUT_USERAGENT_FILTER === '1'

interface ICompanyDetailProps {
    jsToken: string
    wsToken: string
    langCode: ELanguage
    user: IAuthUser
    companyName: string
    isCLP: boolean
    appMode: EMode
    companyProfile: ICompanyInfo | null
    infoPath: TInfoPath
    metaHead: TMetaHead
    companyOCIData?: TInfoCompanyOCI | null
    seoKeywords: TSeoKeywordData[]
}

export enum SelectTab {
    ABOUT_US,
    OPENING_JOB,
    VIDEO,
    BENEFITS
}

const CompanyDetail = (props: ICompanyDetailProps) => {
    const { jsToken, wsToken, langCode, user, appMode, infoPath, metaHead, companyOCIData, seoKeywords } = props
    const { dispatch, state } = useStore()
    const { trackingQueue } = useHog()
    useIdentify(user, msBaseUrl)

    // company info
    const [companyProfile, setCompanyProfile] = useState<ICompanyInfo | null>(props.companyProfile)
    // tab select
    const [selectedTab, setSelectedTab] = useState<SelectTab>(SelectTab.ABOUT_US)
    const [isMobile, setIsMobile] = useState<boolean>(false)
    const jobListWrapperRef = useRef<HTMLDivElement>(null)
    const aboutUsSectionRef = useRef<HTMLDivElement>(null)

    const t = (text: string): string => {
        if (langCode === ELanguage.EN) {
            return text
        } else {
            return translations[text]
        }
    }

    const onTabSelected = (tab: SelectTab) => {
        setSelectedTab(tab)
        const selectedTabRef = tab === SelectTab.ABOUT_US ? aboutUsSectionRef : jobListWrapperRef
        window.scrollTo({})
        Utils.debounceHandler(() => {
            selectedTabRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            })
        }, DELAY_HANDLER_TIME)()
    }

    if (metaHead?.title === '') {
        const now = new Date()
        const current =
            langCode === ELanguage.EN
                ? now.toLocaleDateString('en-US', { month: 'short' }) + ', ' + now.getFullYear()
                : 'T' + (now.getMonth() + 1) + '/' + now.getFullYear()
        metaHead.title = t('{companyName} latest recruitment {now} - VietnamWorks')
            .replace(/\{companyName\}/g, companyProfile?.companyName ?? '')
            .replace('{now}', current)
        metaHead.description = t(
            '{companyName} is recruiting for many attractive positions on VietnamWorks with high salary and good benefits. See jobs at {companyName} and apply now!'
        ).replace(/\{companyName\}/g, companyProfile?.companyName ?? '')
    }

    const handleResize = () => {
        if (typeof window !== 'undefined') {
            if (window.innerWidth > 768) setIsMobile(false)
            else setIsMobile(true)
        }
    }

    const updateUserState = (e: Event) => {
        if (!e?.detail?.user) {
            return
        }
        dispatch({
            type: ACTION_SET_USER,
            payload: e?.detail?.user
        })
        getCompanyDetail(e?.detail?.user?.userId || 0)
    }

    const getCompanyDetail = async (userId: number) => {
        const { meta, data } = await companyDetailService(infoPath, userId, langCode)
        if (data === null || meta.code !== 200) {
            return
        }
        setCompanyProfile(data)
    }

    useEffect(() => {
        trackingQueue({
            eventName: 'js_platform_discovery:company_profile_view',
            properties: {
                companyId: companyProfile?.companyId || '',
                companyIndustry: companyProfile?.industryNames || '',
                companyName: companyProfile?.companyName || '',
                isCcp: !!companyProfile?.clpInfo,
                follower: Number(companyProfile?.totalFollow),
                totalJobs: Number(state.companyJobs?.jobTotal),
                isReport: !!companyProfile?.isReported
            }
        })

        handleResize()
        window.scrollTo(0, 0)
        window.addEventListener('resize', handleResize)
        window.addEventListener(UPDATE_USER, updateUserState)

        return () => {
            window.removeEventListener('resize', handleResize)
            window.removeEventListener(UPDATE_USER, updateUserState)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (user === null) {
            return
        }
        dispatch({
            type: ACTION_SET_USER,
            payload: user
        })
    }, [dispatch, user])

    useEffect(() => {
        dispatch({
            type: ACTION_SET_LANGUAGE,
            payload: langCode
        })
    }, [dispatch, langCode])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }
        const handleSectionVisibilityChangeCallback = Utils.debounceHandler(() => {
            if (!jobListWrapperRef.current) {
                return
            }

            const isJobListSectionInViewPort = jobListWrapperRef.current.getBoundingClientRect().top <= 50

            if (isJobListSectionInViewPort && selectedTab !== SelectTab.OPENING_JOB) {
                setSelectedTab(SelectTab.OPENING_JOB)
            } else if (!isJobListSectionInViewPort && selectedTab === SelectTab.OPENING_JOB) {
                setSelectedTab(SelectTab.ABOUT_US)
            }
        }, 30)

        document.addEventListener('scroll', handleSectionVisibilityChangeCallback)
        return () => document.removeEventListener('scroll', handleSectionVisibilityChangeCallback)
    }, [selectedTab])

    const companyBannerImage =
        companyProfile !== null && companyProfile.bannerDesktopUri !== ''
            ? companyProfile.bannerDesktopUri
            : EMPTY_BANNER_IMAGE

    return (
        <HogProvider
            posthogConfig={{
                key: process.env.NEXT_PUBLIC_POSTHOG_API_KEY ?? '',
                host: process.env.NEXT_PUBLIC_POSTHOG_API_HOST ?? '',
                options: {
                    optOutUserAgentFilter: isOptOutUserAgentFilter
                }
            }}
        >
            <MasterLayout
                seoKeywords={seoKeywords}
                appMode={appMode}
                langCode={langCode}
                jsToken={jsToken}
                wsToken={wsToken}
                isMobileScale={isMobile}
                noOffsetContainer
                metaHead={metaHead}
            >
                <CompanyPageContainer>
                    <ContentWithBordered>
                        <Banner
                            t={t}
                            companyInfo={companyProfile}
                            onTabChange={onTabSelected}
                            selectedTab={selectedTab}
                            companyBannerImage={companyBannerImage}
                        />
                    </ContentWithBordered>
                    <ContentWithBordered ref={aboutUsSectionRef}>
                        <ContentWrapper isPadding id='AboutUs' noObjectFit hasBg>
                            <AboutUs t={t} companyInfo={companyProfile} ociData={companyOCIData} />
                        </ContentWrapper>
                        <ContentWrapper isPadding id='Video' hasBg>
                            <Video t={t} videoUrls={companyOCIData?.companyVideos || companyProfile?.jobVideoURLs} />
                        </ContentWrapper>
                        <ContentWrapper isPadding id='Benefit' hasBg>
                            <Benefit t={t} companyInfo={companyProfile} ociData={companyOCIData} />
                        </ContentWrapper>
                    </ContentWithBordered>
                    <ContentWithBordered>
                        <ContentWrapper ref={jobListWrapperRef} id='OpeningJob' isPadding hasBg>
                            <JobList t={t} companyInfo={companyProfile} enablePagination />
                        </ContentWrapper>
                    </ContentWithBordered>
                </CompanyPageContainer>
                <BreadcrumbStyle>
                    <Anchor
                        href={process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL}
                        name='body'
                        size='small'
                        style={{ cursor: 'pointer' }}
                        color={colors.primary[40]}
                    >
                        {t('Home page')}
                    </Anchor>
                    <NarrowArrow direction='right' size={14} />
                    <Anchor
                        href={`${process.env.NEXT_PUBLIC_VNW_JOBSEEKER_URL}${t('/job-search')}`}
                        name='body'
                        size='small'
                        style={{ cursor: 'pointer' }}
                        color={colors.primary[40]}
                    >
                        {t('Jobs')}
                    </Anchor>
                    <NarrowArrow direction='right' size={14} />
                    <Span name='body' size='small' color={colors.grey[40]}>
                        {t('Job at')} {companyProfile?.companyName}
                    </Span>
                </BreadcrumbStyle>
                <GotoTop trackId='bannerSection' />
            </MasterLayout>
        </HogProvider>
    )
}

export default CompanyDetail
